import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { translations } from '../data/translations';
import type { Locale } from '../data/translations';
type LanguageContextValue = {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: (typeof translations)['en'];
};
const STORAGE_KEY = 'pulse-locale';
const LanguageContext = createContext<LanguageContextValue | null>(null);
function readInitialLocale(): Locale {
  if (typeof window === 'undefined') return 'en';
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored === 'en' || stored === 'es') return stored;
  return window.navigator.language?.toLowerCase().startsWith('es') ? 'es' : 'en';
}
export function LanguageProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [locale, setLocaleState] = useState<Locale>(readInitialLocale);
  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);
  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* storage can be unavailable in private mode — the switch still works in-session */
    }
  }, []);
  const value = useMemo(() => ({
    locale,
    setLocale,
    t: translations[locale]
  }), [locale, setLocale]);
  return <LanguageContext.Provider value={value} data-id="element-221" data-mp="330crpz73sjfa">{children}</LanguageContext.Provider>;
}
export function useLanguage(): LanguageContextValue {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used inside a LanguageProvider');
  }
  return context;
}